import { useSyncExternalStore } from 'react';

/**
 * A role switch in progress in this tab (RoleSwitchOverlay): from the click in
 * the role menu (or the page load adopting another role) until the page
 * reloads. The overlay covers the page meanwhile, so the previous role's page
 * can be neither used nor seen refetching under a cleared session.
 *
 * Driven by roleSelection.ts (switchAssignment, bootstrapAssignment): started
 * at the beginning, ended only when the switch fails (on success the page
 * reloads).
 */
export interface RoleSwitchProgress {
  /** "Switching to Activation Manager – Adidas…" */
  message: string;
}

export const SWITCHING_ROLE_MESSAGE = 'Switching role…';
export const LOADING_ROLE_MESSAGE = 'Loading your role…';
export const PREVIEW_STARTING_MESSAGE = 'Starting the preview…';
export const PREVIEW_ENDING_MESSAGE = 'Ending the preview…';

let progress: RoleSwitchProgress | null = null;
const listeners = new Set<() => void>();

const publish = (next: RoleSwitchProgress | null) => {
  progress = next;
  listeners.forEach((listener) => listener());
};

export const getRoleSwitchProgress = (): RoleSwitchProgress | null => progress;

export const subscribeRoleSwitchProgress = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** "Switching to <label>…" ("Switching role…" without a label). */
export const switchingMessage = (label?: string | null): string => {
  const name = String(label ?? '').trim();
  return name ? `Switching to ${name}…` : SWITCHING_ROLE_MESSAGE;
};

/** Shows the overlay with "Switching to <label>…". */
export const startRoleSwitch = (label?: string | null): void => {
  publish({ message: switchingMessage(label) });
};

/** Shows the overlay with a message of its own (the page load: "Loading your role…"). */
export const showRoleSwitchMessage = (message: string): void => {
  publish({ message });
};

/** Hides the overlay (the switch failed). */
export const endRoleSwitch = (): void => {
  if (progress !== null) publish(null);
};

/** The switch in progress for React, or null. */
export const useRoleSwitchProgress = (): RoleSwitchProgress | null =>
  useSyncExternalStore(subscribeRoleSwitchProgress, getRoleSwitchProgress, getRoleSwitchProgress);
